import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import './App.css';
import { store } from './Redux/stores/store';
import Router from './Route/Router';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { I18nProvider, useI18n } from './Utils/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyUiPreferences,
  getUiPreferences,
  UI_PREFERENCES_EVENT,
} from './Utils/uiPreferences';


const GlobalLoadingOverlay = () => {
  const active = useRef(0);
  const [visible, setVisible] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const start = (event) => {
      const url = String(event?.detail?.url || '');
      // Login/auth and attendance export use their own local loading UI.
      if (/\/(login|auth|signin|attendanceadmin).*export=true/i.test(url) || /\/(login|auth|signin)(\?|$|\/)/i.test(url)) return;
      active.current += 1;
      if (!timer.current) {
        timer.current = window.setTimeout(() => {
          if (active.current > 0) setVisible(true);
          timer.current = null;
        }, 550);
      }
    };
    const end = (event) => {
      const url = String(event?.detail?.url || '');
      if (/\/(login|auth|signin|attendanceadmin).*export=true/i.test(url) || /\/(login|auth|signin)(\?|$|\/)/i.test(url)) return;
      active.current = Math.max(0, active.current - 1);
      if (active.current === 0) {
        if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
        setVisible(false);
      }
    };
    window.addEventListener('bolde-request-start', start);
    window.addEventListener('bolde-request-end', end);
    return () => {
      window.removeEventListener('bolde-request-start', start);
      window.removeEventListener('bolde-request-end', end);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  if (!visible) return null;
  return (
    <div className="app-busy-overlay" role="status" aria-live="polite" aria-label="Sedang memproses">
      <div className="app-busy-card">
        <div className="app-busy-orbit"><span /><span /><span /></div>
        <div className="app-busy-title">Sedang memproses</div>
        <div className="app-busy-subtitle">Mohon tunggu sebentar…</div>
      </div>
    </div>
  );
};

const AppShell = ({ locale }) => {
  const [preferences, setPreferences] = useState(getUiPreferences());
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const duration = prefersReducedMotion ? 80 : 420;
    const timer = window.setTimeout(() => setShowSplash(false), duration);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    applyUiPreferences(preferences);

    const handlePreferenceChange = (event) => {
      const next = event.detail || getUiPreferences();
      setPreferences(next);
      applyUiPreferences(next);
    };

    window.addEventListener(UI_PREFERENCES_EVENT, handlePreferenceChange);
    return () => window.removeEventListener(UI_PREFERENCES_EVENT, handlePreferenceChange);
  }, [preferences]);

  const algorithms = useMemo(() => {
    const list = [];
    if (preferences.mode === 'dark') list.push(antdTheme.darkAlgorithm);
    if (preferences.compact) list.push(antdTheme.compactAlgorithm);
    return list.length ? list : antdTheme.defaultAlgorithm;
  }, [preferences.mode, preferences.compact]);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        algorithm: algorithms,
        token: {
          colorPrimary: preferences.primaryColor,
          borderRadius: 10,
        },
      }}
    >
      <HelmetProvider>
        <Provider store={store}>
          <GlobalLoadingOverlay />
          {showSplash && (
            <div className="app-opening-splash" role="status" aria-label="Membuka BOLDe Access">
              <div className="app-opening-splash__ambient app-opening-splash__ambient--one" />
              <div className="app-opening-splash__ambient app-opening-splash__ambient--two" />
              <div className="app-opening-splash__content">
                <div className="app-opening-splash__logo-shell">
                  <span className="app-opening-splash__ring" />
                  <img src="/assets/logo.png" alt="BOLDe Access" className="app-opening-splash__logo" />
                </div>
                <div className="app-opening-splash__brand">BOLDe Access</div>
                <div className="app-opening-splash__caption">Employee Workspace</div>
                <div className="app-opening-splash__loader"><span /></div>
              </div>
            </div>
          )}
          <div className={showSplash ? 'app-shell app-shell--entering' : 'app-shell app-shell--ready'}>
            <Router />
          </div>
        </Provider>
      </HelmetProvider>
    </ConfigProvider>
  );
}

const App = () => (
  <I18nProvider>
    <AppWithI18n />
  </I18nProvider>
);

const AppWithI18n = () => {
  const { locale } = useI18n();
  return <AppShell locale={locale} />;
};

export default App;
